import { Button, COLOR_PALETTE, Flex, Text, desktopFirstMediaQuery } from '@inflearn/ds-react-next';
import { useOnboardingContext } from '@inflearn/modals';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import type { OnboardingAnswerResponse } from '../../../../../../resources/@types/uccSchema';

const OnboardingModal = dynamic(() => import('appShell/OnboardingModal'), {
  ssr: false,
});

interface OnboardingModalReentrySectionProps {
  userInterests?: OnboardingAnswerResponse[];
}

const InterestsEditCard = ({
  userInterests,
  onClick,
}: {
  userInterests: OnboardingAnswerResponse[];
  onClick: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Flex
      w="100%"
      py={24}
      px={32}
      align="center"
      justify="space-between"
      gap={20}
      styles={{
        root: {
          border: `1px solid ${COLOR_PALETTE.gray[3]}`,
          borderRadius: 16,
          [desktopFirstMediaQuery.mobile]: { padding: 24 },
        },
      }}
    >
      <Flex direction="column">
        <Text size="md" fw={700} c="gray.9">
          {t('관심사 편집')}
        </Text>
        <Text size="sm" fw={500} c="gray.6" lineClamp={1}>
          {userInterests
            .map((i) => (i.type === 'UNKNOWN' ? t('잘 모르겠다') : i.content))
            .filter(Boolean)
            .join(', ')}
        </Text>
      </Flex>
      <Button variant="default" color="infgreen" size="sm" radius="md" onClick={onClick}>
        {t('다시 설정')}
      </Button>
    </Flex>
  );
};

const CourseRecommendationCard = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <Flex
      w="100%"
      py={24}
      px={32}
      align="center"
      gap={16}
      styles={{
        root: {
          background:
            'linear-gradient(-21.7deg, rgb(230, 249, 242) 32.6%, rgb(227, 250, 252) 67.4%)',
          borderRadius: 16,
          [desktopFirstMediaQuery.mobile]: { padding: 24 },
        },
      }}
    >
      <Flex
        direction="column"
        gap={4}
        styles={{
          root: { flex: 1 },
        }}
      >
        <Text size="xs" fw={400} c="cyan.6">
          {t('맞춤 강의를 찾아드려요')}
        </Text>
        <Text
          size="sm"
          fw={700}
          styles={{
            root: {
              background: 'linear-gradient(90deg, #15AABF, #00C471)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            },
          }}
        >
          {t('어떤 강의가 나한테 맞을까?')}
        </Text>
      </Flex>
      <Button variant="filled" color="infgreen" size="xs" radius="md" onClick={onClick}>
        {t('강의 추천받기')}
      </Button>
    </Flex>
  );
};

export const OnboardingModalReentrySection = ({
  userInterests,
}: OnboardingModalReentrySectionProps) => {
  const { isModalOpen, openModal, closeModal, skipOnboarding, entryPoint, initialView } =
    useOnboardingContext();

  const handleClick = () => {
    openModal({
      initialView: 'onboarding',
      entryPoint: 'my_settings',
    });
  };

  const hasCategories = userInterests && userInterests.length > 0;

  return (
    <>
      {hasCategories ? (
        <InterestsEditCard userInterests={userInterests} onClick={handleClick} />
      ) : (
        <CourseRecommendationCard onClick={handleClick} />
      )}
      <OnboardingModal
        opened={isModalOpen}
        onSkip={skipOnboarding}
        onClose={closeModal}
        entryPoint={entryPoint}
        isFirstSignUp={false}
        initialView={initialView}
      />
    </>
  );
};
